import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lightbulb, Loader2, Mic, Send, Square } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCaptureRecorder } from "@/hooks/useCaptureRecorder";
import { SuggestionCard } from "@/components/conecta/SuggestionCard";
import { ContactSummary } from "@/components/conecta/ContactSummary";

const EXAMPLES = ["Necesito un abogado fiscal", "Alguien que llegue a gasolineras", "¿Qué tengo con "];

const VoiceButton = ({ onText, disabled }) => {
  const r = useCaptureRecorder();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (r.state === "idle") return r.start();
    const out = await r.stop();
    if (!out || out.blob.size < 1000) return toast.error("Grabación muy corta.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", out.blob, `pregunta.${out.ext}`);
      const { data } = await api.post("/cruce/transcribe", fd);
      onText(data.text);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const rec = r.state !== "idle";
  return (
    <button data-testid="cruce-voice-btn" type="button" disabled={disabled || busy} onClick={toggle} aria-label={rec ? "Detener" : "Preguntar por voz"}
      className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 ${rec ? "bg-red-600 rec-ring" : "bg-[#dc2626]"}`}>
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : rec ? <Square className="w-5 h-5 fill-white" /> : <Mic className="w-5 h-5" />}
    </button>
  );
};

const NoResults = ({ text, query }) => {
  const [saved, setSaved] = useState(false);
  const save = async () => {
    try { await api.post("/needs", { text: query }); setSaved(true); toast.success("Necesidad abierta guardada"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="c-card p-4 space-y-3" data-testid="cruce-need-box">
      {text && <p data-testid="cruce-no-results" className="text-sm c-text-2">{text}</p>}
      <button data-testid="cruce-save-need-btn" disabled={saved} onClick={save} className="w-full py-2.5 rounded-xl c-card text-sm font-semibold flex items-center justify-center gap-1.5">
        <Lightbulb className="w-4 h-4 c-accent" /> {saved ? "Guardada en Necesidades" : "Guardar como necesidad abierta"}
      </button>
    </div>
  );
};

const Consulta = ({ res }) => {
  const [summary, setSummary] = useState(res.summary);
  const pick = (id) => api.get(`/contacts/${id}/summary`).then((r) => setSummary(r.data)).catch((e) => toast.error(errMsg(e)));
  if (summary) return <ContactSummary s={summary} />;
  if (!res.candidates.length) return <p data-testid="consulta-not-found" className="c-card p-4 text-sm c-text-2">No encontré a "{res.name}" en tu red.</p>;
  return (
    <div className="space-y-2" data-testid="consulta-candidates">
      <p className="text-sm c-text-2">¿A quién te refieres?</p>
      {res.candidates.map((c) => (
        <button key={c.id} data-testid="consulta-candidate-btn" onClick={() => pick(c.id)} className="w-full text-left c-card p-3 text-sm font-semibold">
          {c.name}{c.company ? <span className="c-text-2 font-normal"> · {c.company}</span> : null}
        </button>
      ))}
    </div>
  );
};

const speak = (text) => {
  try { const u = new SpeechSynthesisUtterance(text); u.lang = "es-MX"; window.speechSynthesis.cancel(); window.speechSynthesis.speak(u); } catch (e) { console.error("speak: lectura en voz alta no disponible", e?.name); }
};

export default function CrucePage() {
  const [params] = useSearchParams();
  const [text, setText] = useState(params.get("q") || "");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const readAloud = useRef(false);
  useEffect(() => { api.get("/me").then((r) => { readAloud.current = r.data.prefs.read_aloud; }).catch((e) => console.error("CrucePage: no se pudieron leer las preferencias", e?.response?.status ?? e?.name)); }, []);

  const run = useCallback(async (q) => {
    if (!q || q.trim().length < 3) return;
    setLoading(true);
    setRes(null);
    try {
      const { data } = await api.post("/cruce", { text: q.trim() });
      setRes(data);
      if (readAloud.current) speak(data.kind === "consulta" ? data.summary?.headline || "No encontré a ese contacto." : data.suggestions.length ? `Te sugiero a ${data.suggestions.map((s) => s.contact.name).join(", ")}.` : data.no_results);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { const q = params.get("q"); if (q) run(q); }, [params, run]);

  return (
    <div data-testid="cruce-page" className="space-y-4">
      <form onSubmit={(e) => { e.preventDefault(); run(text); }} className="space-y-2">
        <div className="flex items-end gap-2">
          <textarea data-testid="cruce-input" rows={2} className="c-input flex-1 resize-none" placeholder="¿Qué necesitas? Ej. alguien que haga avalúos en Querétaro"
            value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); run(text); } }} />
          <VoiceButton disabled={loading} onText={(t) => { setText(t); run(t); }} />
        </div>
        <button data-testid="cruce-submit-btn" type="submit" disabled={loading || text.trim().length < 3} className="c-btn-primary w-full py-3 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Dame una opción
        </button>
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((x) => <button type="button" key={x} onClick={() => setText(x)} className="text-[11px] px-2.5 py-1 rounded-full bg-[var(--c-elev)] c-text-2">{x}</button>)}
        </div>
      </form>
      {loading && <p data-testid="cruce-loading" className="text-sm c-muted text-center py-6">Buscando en tu red…</p>}
      {res?.kind === "consulta" && <Consulta key={res.query} res={res} />}
      {res?.kind === "cruce" && (
        <div className="space-y-3" data-testid="cruce-results">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Para: {res.need} · {res.candidates_considered} contactos revisados</p>
          {res.suggestions.map((s, i) => <SuggestionCard key={s.contact.id} s={s} i={i} need={res.need} query={res.query} />)}
          <NoResults text={res.no_results} query={res.query} />
        </div>
      )}
    </div>
  );
}
